import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import useSEO from '../../../hooks/useSEO'
import SectionNav, { type SectionNavItem } from '../../../components/SectionNav'
import CardGallery from '../../../components/CardGallery.tsx'
import BotsCatalogSearch, { type CatalogSearchGroup } from '../components/BotsCatalogSearch.tsx'
import { matchesQuery } from '../utils/searchMatch'
import FeedSkeleton from '../../../components/FeedSkeleton'
import { usePopularSections, toolCategoriesOf, type PopularSectionBase } from '../hooks/usePopularSections'
import { rawCardsOf, useFeedBlocks, type FeedBlock } from '../hooks/useFeedBlocks.ts'
import FeedBlocks from '../components/FeedBlocks.tsx'
import TrendGalleryBlock from '../components/TrendGalleryBlock.tsx'
import { useToolCardLanding } from '../hooks/useToolCardLanding.ts'
import { toolCardSeoTitle, toolCardSeoDescription, cardPrimaryUrl, cardCtxUrl, cardSearchHitUrl } from '../utils/cardSeo.ts'
import { pendingToolRunPath } from '../utils/activeToolRun'
import { useBotsWidget } from '../context/BotsWidgetContext.tsx'
import { useProviders } from '../../../hooks/useProviders.ts'
import { useSelectedModel } from '../../../context/SelectedModelContext.tsx'
import {
	applyPopularItem,
	makeFindModel,
	rawCardToItem,
	trendMemberSearchHits,
	type BackendCard,
} from '../utils/popularCardApply.ts'
import { backendCardToGalleryCard } from '../utils/cardGalleryItem.ts'
import type { GenerationCard } from '../../../types/generationCard.ts'
import {
	TREND_ZONES,
	ZONE_SECTION_TYPES,
	cardTrendZones,
	landingZoneOf,
	parseTrendsPath,
	sectionTrendZones,
	zoneLabel,
	zoneLandingCtx,
	zonePath,
	type TrendZone,
} from '../utils/trendZones.ts'

// Страница «Тренды» — отдельный раздел на общем движке инструментов
// (popular_category, лейн 'trends'). Карточки хранятся в едином пуле и
// отмечают лейн «Тренды» в админке; сюда попадают те, у кого он проставлен.
// Подразделы (пеблы) = config.tool_categories['trends'] — тот же механизм,
// что у /bots/images.
//
// Две зоны (владелец 16.09): капсулы «Видео | Фото». Зона = блоки конструктора
// (ленты trend_feed и галереи trend_gallery), у которых стоит эта зона, в
// админском порядке. Галерея — обычный блок: мозаика карточек зоны (карточка
// отмечает зоны галочками, подсказка по медиа — только умолчание). Подразделы
// второй строки навигации есть у зоны с галереей и фильтруют её.
// Легаси (страница без блоков и галерей): пеблы + одна мозаика, как до зон.

// Конвертер карточки в плитку галереи — общий с Фотостудией (см.
// utils/cardGalleryItem.ts); страница задаёт только свой лейн для href.
const trendCardToGalleryCard = (card: BackendCard, hrefCtx?: string | null): GenerationCard =>
	backendCardToGalleryCard(card, 'trends', hrefCtx)

type ZoneItem =
	| { kind: 'feed'; block: FeedBlock }
	| {
			kind: 'gallery'
			section: PopularSectionBase
			title: string
			source: string
			category: string | null
			limit: number
			hidden: boolean
	  }

/** Блоки зоны в админском порядке: ленты (уже собранные useFeedBlocks) и галереи. */
function buildZoneItems(sections: PopularSectionBase[] | null, feedBlocks: FeedBlock[], zone: TrendZone): ZoneItem[] {
	const feedById = new Map(feedBlocks.map((b) => [b.section.id, b]))
	const out: ZoneItem[] = []
	for (const sec of sections ?? []) {
		if (!ZONE_SECTION_TYPES.has(sec.section_type)) continue
		if (!sectionTrendZones(sec.config).includes(zone)) continue
		if (sec.section_type === 'trend_feed') {
			const block = feedById.get(sec.id)
			if (block) out.push({ kind: 'feed', block })
			continue
		}
		const cfg = sec.config
		out.push({
			kind: 'gallery',
			section: sec,
			title: String(cfg.title || 'Все тренды'),
			source: String(cfg.source || 'zone'),
			category: cfg.source_category ? String(cfg.source_category) : null,
			limit: Math.max(0, Math.round(Number(cfg.visible_count) || 0)),
			hidden: (sec as { is_visible?: boolean }).is_visible === false,
		})
	}
	return out
}

/** Состав галереи по её источнику: все карточки зоны, категория или ⭐-популярные. */
function galleryCards(item: Extract<ZoneItem, { kind: 'gallery' }>, zoneCards: BackendCard[]): BackendCard[] {
	if (item.source === 'category') {
		return item.category ? zoneCards.filter((c) => c.category_by_page?.trends === item.category) : []
	}
	if (item.source === 'popular') return zoneCards.filter((c) => !!c.is_popular)
	// «Только в своей категории» (hide_from_all) — не в общую мозаику, как у пебла «Все».
	return zoneCards.filter((c) => !(c.hide_from_all && c.category_by_page?.trends))
}

export default function BotsTrends() {
	const { '*': wildcardRaw, cardSlug: cardSlugParam, galleryCtx } = useParams()
	// Контекст в пути лендинга (/bots/trends/<ctx>/tool/<slug>) подменяет
	// wildcard: зона/пебл после клика по карточке не перескакивают. Корневой
	// /tool/<slug> открывается поверх корня (canonical всегда корневой).
	const wildcardSlug = galleryCtx ?? wildcardRaw
	// Зоны: хвост пути разбирает parseTrendsPath; лендинг может прийти и хвостом
	// wildcard-маршрута (/photo/<категория>/tool/<slug> — двухсегментный контекст).
	const parsed = useMemo(() => parseTrendsPath(wildcardSlug), [wildcardSlug])
	const cardSlug = cardSlugParam ?? parsed.cardSlug ?? undefined
	const { handleStyleApply } = useBotsWidget()
	const { brands } = useProviders()
	const { selectModel } = useSelectedModel()
	const navigate = useNavigate()

	// Возврат в раздел с НЕЗАКРЫТОЙ работой: пин карточки не переживает поход
	// в другой раздел (там своя модель → mismatch снимает пин), а job тренда
	// живёт. Без этого юзер возвращался к стандартному виджету, хотя фото уже
	// сгенерировано и оплачено (жалоба владельца 2026-07-24). Возвращаем на
	// тот же лендинг — инструмент откроется со своей историей. Закрыл
	// крестиком → job сброшен → путь пуст, ничего не всплывает.
	useEffect(() => {
		if (cardSlug) return
		const pending = pendingToolRunPath('trends')
		if (pending && pending !== window.location.pathname) navigate(pending, { replace: true })
	}, [cardSlug, navigate])
	// Лендинг /bots/trends/tool/{slug}: применяет карточку в виджет (одношаг и
	// пайплайн — тот же useToolCardLanding, что на /bots/images) + якорь к плитке.
	const toolLanding = useToolCardLanding('trends', cardSlug)
	const findModel = useMemo(() => makeFindModel(brands), [brands])
	const [searchQuery, setSearchQuery] = useState('')

	// Стафф-превью (?preview=1): бэк отдаёт и скрытые/вне расписания блоки —
	// «посмотреть как увидит юзер» до включения глазика.
	const location = useLocation()
	const isPreview = new URLSearchParams(location.search).get('preview') === '1'
	// Подразделы трендов = tool_categories['trends'] (общий кэш-хук — fetch
	// ?page=trends делается один раз на страницу). all: нужны и trend_feed /
	// trend_gallery — блоки зон; popular_category-логика ниже фильтрует сама.
	const { sections, loading } = usePopularSections('trends', { all: true, preview: isPreview })
	const toolCats = useMemo(() => toolCategoriesOf(sections, 'trends'), [sections])

	// Пул всех видимых тренд-карточек (шире активного пебла) — для поиска и
	// вывода сетки. Фильтр = тот же, что в PopularModelsTab (лейн + скрытие).
	const trendsPool = useMemo(() => {
		const out: BackendCard[] = []
		for (const sec of sections ?? []) {
			const cards = Array.isArray(sec.config.cards) ? (sec.config.cards as BackendCard[]) : []
			for (const card of cards) {
				const visible =
					(!card.target_pages?.length || card.target_pages.includes('trends')) &&
					!card.hidden_pages?.includes('trends')
				if (visible) out.push(card)
			}
		}
		return out
	}, [sections])

	// Пул членства блоков — ВСЕ карточки всех секций, БЕЗ лейн-фильтра: лейны/
	// глазики управляют сеткой галереи и из блока карточку не выкидывают
	// (зеркало HomeTemp и админ-превью ленты).
	const rawPoolAll = useMemo(() => rawCardsOf(sections), [sections])

	// Блоки-ленты (секции trend_feed) в порядке админки — по всем зонам сразу.
	const feedBlocks = useFeedBlocks({ page: 'trends', sections, rawPool: rawPoolAll, lanePool: trendsPool })

	// Режим зон: на странице есть хоть одна лента или галерея. Без них —
	// легаси-галерея с пеблами (deploy-safe: страница без блоков не меняется).
	const zoneMode = useMemo(() => (sections ?? []).some((s) => ZONE_SECTION_TYPES.has(s.section_type)), [sections])
	// Зона лендинга, пришедшего БЕЗ зоны в адресе (ссылка с главной, из поиска, разосланная раньше):
	// открываем ту зону, где карточка реально лежит. В обеих зонах или ни в одной — корневая, как раньше.
	const landingZone = useMemo(() => {
		if (parsed.explicitZone || !cardSlug) return null
		const card = trendsPool.find((c) => c.slug === cardSlug)
		return card ? landingZoneOf(card) : null
	}, [parsed.explicitZone, cardSlug, trendsPool])
	const zone = landingZone ?? parsed.zone
	const zoneCategory = parsed.category
	// Карточки зоны: явные галочки карточки, иначе подсказка по медиа.
	const zoneCards = useMemo(() => trendsPool.filter((c) => cardTrendZones(c).includes(zone)), [trendsPool, zone])
	const zoneItems = useMemo(() => buildZoneItems(sections, feedBlocks, zone), [sections, feedBlocks, zone])
	const zoneHasGallery = zoneItems.some((it) => it.kind === 'gallery')
	// Подразделы зоны — только те категории, в которых есть её карточки.
	const zoneCats = useMemo(
		() => toolCats.filter((tc) => zoneCards.some((c) => c.category_by_page?.trends === tc.slug)),
		[toolCats, zoneCards],
	)

	useSEO(
		toolLanding.card
			? {
					title: toolCardSeoTitle(toolLanding.card),
					description: toolCardSeoDescription(toolLanding.card),
					canonical: `https://umnik.ai${cardPrimaryUrl(toolLanding.card) || '/bots/trends'}`,
				}
			: zoneMode && zone === 'photo'
				? {
						title: 'Фото-тренды нейросетей — вирусные образы из одного фото | Umnik.AI',
						description:
							'Фото-тренды под ключ: загрузи фото — получи вирусный образ. Десятки сценариев, которые обновляются под повестку.',
						canonical: 'https://umnik.ai/bots/trends/photo',
					}
				: {
						title: 'Тренды нейросетей — вирусные образы из одного фото | Umnik.AI',
						description:
							'Готовые тренды под ключ: загрузи фото — получи вирусный образ или оживи его в видео. Десятки сценариев, которые обновляются под повестку.',
						canonical: 'https://umnik.ai/bots/trends',
					},
	)

	// ── Легаси (без блоков и галерей): пеблы Популярные/Все/категории + мозаика ──
	// Есть ли хоть один ⭐-тренд — от этого зависит дефолтный пебл («Популярные»
	// vs «Все») и появление пебла «Популярные».
	const hasPopularTools = useMemo(() => trendsPool.some((c) => c.is_popular), [trendsPool])
	// URL пебла: /bots/trends/<slug>. Неизвестный слаг оставляет вид «Все».
	const activeToolSlug =
		wildcardSlug && toolCats.some((tc) => tc.slug === wildcardSlug) ? wildcardSlug : undefined
	const popularOnly = !wildcardSlug && hasPopularTools

	// Плитки легаси-галереи: активный пебл. Поиск сюда НЕ вмешивается (решение
	// владельца 2026-07-27): выдача живёт только в дропдауне, страница под ним
	// остаётся такой, какой была — раньше ввод перетряхивал сетку и прятал
	// демо-блок ещё до того, как юзер что-то выбрал.
	const { items, rawById } = useMemo(() => {
		const items: GenerationCard[] = []
		const rawById = new Map<string, BackendCard>()
		for (const card of trendsPool) {
			const inView = popularOnly
				? !!card.is_popular
				: activeToolSlug
					? card.category_by_page?.['trends'] === activeToolSlug
					: !(card.hide_from_all && card.category_by_page?.['trends'])
			if (!inView) continue
			const gc = trendCardToGalleryCard(card, activeToolSlug ?? (popularOnly ? 'popular' : 'all'))
			items.push(gc)
			rawById.set(gc.id, card)
		}
		return { items, rawById }
	}, [trendsPool, activeToolSlug, popularOnly])

	// Контекст лендинга ТЕКУЩЕГО вида: в зонах — зона (и подраздел), кроме
	// корня «Видео» (канонический /tool/<slug>); в легаси — активный пебл.
	// Канонику двойники не плодят (canonical — корневой).
	const landingCtx = zoneMode
		? zoneLandingCtx(zone, zoneCategory)
		: (activeToolSlug ?? (popularOnly ? 'popular' : 'all'))
	const ctxToolUrl = (raw: BackendCard, ctx: string | null): string | null =>
		cardCtxUrl(raw, 'trends', ctx)

	// Клик = переход на СТРАНИЧНЫЙ лендинг: путь появляется в адресной строке,
	// юзер ОСТАЁТСЯ на текущей вкладке трендов — лендинг применяет карточку в
	// виджет и подсвечивает плитку якорем. Без слага — легаси-применение на
	// месте. Общий канал для галереи и блоков.
	const openCard = (raw: BackendCard) => {
		const url = ctxToolUrl(raw, landingCtx)
		if (url) {
			// skipAnchor: кликнувший УЖЕ смотрит на плитку — авто-скролл якоря
			// нужен только пришедшим по ссылке извне.
			navigate(url, { state: { skipAnchor: true } })
			return
		}
		applyPopularItem(rawCardToItem(raw), undefined, {
			category: 'trends',
			findModel,
			handleStyleApply,
			selectModel,
			navigate,
		})
	}
	// Мульти-тренд (23.10): клик по кадру версии на плитке — тот же лендинг группы, но сразу с этой версией.
	const openCardMember = (raw: BackendCard, memberId: string) => {
		const url = ctxToolUrl(raw, landingCtx)
		if (url) {
			navigate(url, { state: { trendMemberId: memberId, skipAnchor: true } })
			return
		}
		applyPopularItem(rawCardToItem(raw), undefined, {
			category: 'trends',
			findModel,
			handleStyleApply,
			selectModel,
			navigate,
			trendMemberId: memberId,
		})
	}

	const handlePick = (gc: GenerationCard) => {
		const raw = rawById.get(gc.id)
		if (raw) openCard(raw)
	}

	// Группа дропдауна поиска: матч по названию/описанию.
	// Клик = переход на ЛЕНДИНГ карточки (см. подробный комментарий в
	// BotsImages): лендинг сам открывает нужную категорию, ставит карточку
	// в виджет и демо-блок, скроллит к плитке и подсвечивает её.
	const searchGroups = useMemo<CatalogSearchGroup[]>(() => {
		const q = searchQuery.trim()
		if (!q) return []
		// Участники мульти-трендов в пуле спрятаны — ищем и их, находка ведёт на лендинг группы с этим участником.
		const memberHits = trendMemberSearchHits(trendsPool).filter((h) => matchesQuery(q, h.title))
		return [
			{
				key: 'trends',
				title: 'Тренды',
				items: [
					...trendsPool
						.filter((c) => matchesQuery(q, c.title, c.description))
						.map((c) => ({
							id: c.id,
							title: c.title || 'Без названия',
							previewUrl: c.background_preview_url || c.background_url,
						})),
					...memberHits.map((h) => ({ id: h.id, title: h.title, previewUrl: h.previewUrl })),
				],
				onPick: (id) => {
					const hit = memberHits.find((h) => h.id === id)
					if (hit) {
						const url = cardSearchHitUrl(hit.group, 'trends')
						if (url) navigate(url, { state: { trendMemberId: hit.id, skipAnchor: true } })
						return
					}
					const raw = trendsPool.find((c) => c.id === id)
					if (!raw) return
					const url = cardSearchHitUrl(raw, 'trends')
					if (url) {
						navigate(url)
						return
					}
					// Карточка без слага — лендинга нет, остаётся прямое применение.
					applyPopularItem(rawCardToItem(raw), undefined, {
						category: 'trends',
						findModel,
						handleStyleApply,
						selectModel,
						navigate,
					})
				},
			},
		]
	}, [searchQuery, trendsPool, findModel, handleStyleApply, selectModel, navigate])

	// Пеблы легаси-галереи (Популярные/Все/категории) — единственный L1, как до зон.
	const galleryPebls: SectionNavItem[] = useMemo(
		() => [
			...(hasPopularTools
				? [
						{ key: '__popular-tools', label: 'Популярные', to: '/bots/trends', end: true },
						{ key: '__all-tools', label: 'Все', to: '/bots/trends/all' },
					]
				: [{ key: '__all-tools', label: 'Все', to: '/bots/trends', end: true }]),
			...toolCats.map((tc) => ({ key: tc.slug, label: tc.label, to: `/bots/trends/${tc.slug}` })),
		],
		[toolCats, hasPopularTools],
	)
	const galleryPeblSelected =
		activeToolSlug ?? (wildcardSlug === 'all' || !hasPopularTools ? '__all-tools' : '__popular-tools')

	// Навигация зон: капсулы «Видео | Фото» + поиск; подразделы — у зоны с галереей.
	const zoneL1: SectionNavItem[] = useMemo(
		() => TREND_ZONES.map((z) => ({ key: z.key, label: z.label, to: z.path, end: z.key === TREND_ZONES[0].key })),
		[],
	)
	const zoneL2: SectionNavItem[] = useMemo(
		() => [
			{ key: '__all', label: 'Все', to: zonePath(zone), end: true },
			...zoneCats.map((tc) => ({ key: tc.slug, label: tc.label, to: zonePath(zone, tc.slug) })),
		],
		[zone, zoneCats],
	)

	// Старые адреса галереи (/all, /popular, /<категория>) в режиме зон ведут в
	// зону, где эта категория есть (иначе в «Фото» — там галерея по умолчанию).
	// Лендинги карточек (cardSlug) не трогаем — это SEO-страницы.
	const legacyRedirect =
		zoneMode && parsed.legacy && !cardSlug
			? parsed.legacy === 'all' || parsed.legacy === 'popular'
				? zonePath('photo')
				: trendsPool.some((c) => cardTrendZones(c).includes('photo') && c.category_by_page?.trends === parsed.legacy)
					? zonePath('photo', parsed.legacy)
					: zonePath('video', parsed.legacy)
			: null
	if (legacyRedirect) {
		return <Navigate to={legacyRedirect} replace />
	}

	const renderZone = (): ReactNode => {
		if (zoneCategory) {
			// Подраздел: только карточки зоны этой категории, блоки не показываем —
			// категория читается как список.
			const cat = toolCats.find((tc) => tc.slug === zoneCategory)
			const list = zoneCards.filter((c) => c.category_by_page?.trends === zoneCategory)
			if (list.length === 0) {
				return (
					<div className="bots-empty">
						<p className="bots-empty__title">В этом подразделе пока пусто</p>
					</div>
				)
			}
			return <TrendGalleryBlock title={cat?.label ?? zoneCategory} cards={list} limit={0} landingCtx={landingCtx} onPick={openCard} onPickMember={openCardMember} />
		}
		if (zoneItems.length === 0) {
			return (
				<div className="bots-empty">
					<p className="bots-empty__title">В зоне «{zoneLabel(zone)}» пока пусто</p>
				</div>
			)
		}
		// Ленты подряд рисуем одной колонкой FeedBlocks, галереи — своими блоками между ними.
		const out: ReactNode[] = []
		let run: FeedBlock[] = []
		const flush = () => {
			if (run.length === 0) return
			out.push(
				<FeedBlocks
					key={`feeds-${run[0].section.id}`}
					page="trends"
					blocks={run}
					rawPool={rawPoolAll}
					isPreview={isPreview}
					landingCtx={landingCtx}
				/>,
			)
			run = []
		}
		for (const it of zoneItems) {
			if (it.kind === 'feed') {
				run.push(it.block)
				continue
			}
			flush()
			out.push(
				<TrendGalleryBlock
					key={it.section.id}
					title={it.title}
					cards={galleryCards(it, zoneCards)}
					limit={it.limit}
					landingCtx={landingCtx}
					onPick={openCard}
					onPickMember={openCardMember}
					hiddenPreview={isPreview && it.hidden}
				/>,
			)
		}
		flush()
		return <>{out}</>
	}

	return (
		<div className="bots-trends-page">
			{/* SEO/a11y: единственный h1 страницы (визуального заголовка нет). */}
			<h1 className="sr-only">
				{zoneMode && zone === 'photo'
					? 'Фото-тренды нейросетей — вирусные образы из одного фото онлайн'
					: 'Тренды нейросетей — вирусные образы из одного фото онлайн'}
			</h1>
			{zoneMode ? (
				<SectionNav
					l1Items={zoneL1}
					selectedL1={zone}
					onSelectL1={() => setSearchQuery('')}
					l1AriaLabel="Зоны трендов"
					l2Items={zoneHasGallery ? zoneL2 : undefined}
					selectedL2={zoneHasGallery ? (zoneCategory ?? '__all') : undefined}
					onSelectL2={() => undefined}
					showL2={zoneHasGallery}
					l2AriaLabel="Подразделы галереи трендов"
					search={
						<BotsCatalogSearch
							query={searchQuery}
							onQueryChange={setSearchQuery}
							groups={searchGroups}
							placeholder="Поиск по трендам…"
						/>
					}
				/>
			) : (
				<SectionNav
					l1Items={galleryPebls}
					selectedL1={galleryPeblSelected}
					onSelectL1={() => undefined}
					l1AriaLabel="Подразделы трендов"
					search={
						<BotsCatalogSearch
							query={searchQuery}
							onQueryChange={setSearchQuery}
							groups={searchGroups}
							placeholder="Поиск по трендам…"
						/>
					}
				/>
			)}

			{loading ? (
				/* Секции ещё едут: держим место, иначе тело страницы пустое и это
				   читается как «сломалось» (владелец 13.09). */
				<FeedSkeleton />
			) : zoneMode ? (
				renderZone()
			) : (
				/* Легаси: галерея на всю ширину панели (masonry — натуральные пропорции). */
				<div className="bots-trends-gallery">
					<CardGallery
						items={items}
						layout="masonry"
						cardClickAction="primary"
						onPrimaryAction={handlePick}
					/>
				</div>
			)}
		</div>
	)
}
